import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormControlLabel, FormGroup, Menu, MenuItem, Switch, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import { captableAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { captableUrl } from 'common/config/urls';
import { DEFAULT_CURRENCY } from 'common/constants/cap-table';
import {
  BREAKPOINT_ANALYSIS_LOCKED_MESSAGE,
  CAP_TABLE_NEEDS_MORE_SECURITIES_TAGLINE,
  CAP_TABLE_NEEDS_MORE_SECURITIES_TITLE,
  FORBIDDEN_BP_ANALYSIS,
  GET_BP_ACCESS,
} from 'common/constants/cap-table/capTable';
import { CONVERTIBLE_NOTES, UNISSUED_OPTIONS } from 'common/constants/securityTypes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, CustomContainer, MessageBox } from 'components';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import { invokeNavigationSave } from 'components/ui/navigationActionFeedback';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import GoToCapTable from 'pages/CapTable/cap-table/components/GoToCapTable';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { CaptableHk } from 'services/hooks';
import { worldCurrencies } from 'utilities';
import { BreakpointAnalysisProps } from './BreakpointAnalysis.props';
import { classes, Root } from './BreakpointAnalysis.styles';
import useCapTableTertiaryNavItems from '../useCapTableTertiaryNavItems';
import CustomBreakpoints from './components/CustomBreakpoints/CustomBreakpoints';
import WaterfallBreakpoints, { WaterfallSaveState } from './components/WaterfallBreakpoints/WaterfallBreakpoints';
import {
  BreakpointColumn,
  BreakpointSeries,
  CustomBreakpointsData,
  CustomBreakpointsSaveState,
} from './types/breakpoint-analysis.models';
import replaceProRata from './utilities/replaceProRata';

const isCustomSaveState = (
  state: WaterfallSaveState | CustomBreakpointsSaveState | null
): state is CustomBreakpointsSaveState => state !== null && 'onProformaOpen' in state;

const BreakpointAnalysis = (props: BreakpointAnalysisProps) => {
  const [globalStore] = useStore() as unknown as UseStoreValues;

  const { viewOnlyUser, isExportMode } = useLayoutContext();
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const { isShowLoadingProgress, captableInfo: capTableInStore } = globalStore;
  const captableInfo = useMemo(() => capTableInStore ?? {}, [capTableInStore]);
  const [waterfallColumns, setWaterfallColumns] = useState<BreakpointColumn[] | null>(null);
  const [customBreakpointColumns, setCustomBreakpointColumns] = useState<BreakpointColumn[] | null>([]);
  const [showSecurityTypeMsg, setShowSecurityTypeMsg] = useState(false);
  const [customBreakpoints, setCustomBreakpoints] = useState(captableInfo?.use_custom_breakpoints || false);
  const [isDisabled, setIsDisabled] = useState<boolean>();
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>();
  const [breakpointSaveState, setBreakpointSaveState] = useState<WaterfallSaveState | CustomBreakpointsSaveState | null>(null);
  const [saveMenuAnchorEl, setSaveMenuAnchorEl] = useState<HTMLElement | null>(null);

  const { useGetBreakpointAnalysis } = CaptableHk;
  const { capTableInfo: updatedCaptableInfo, getCapTableBreakpoints } = useGetBreakpointAnalysis();
  const { selectedMeasurementDate, saveAsNewProforma, isCreatingProforma } = props;
  const [format] = useFormat({ page: 'captable', units: REGULAR_UNIT });
  const { currency } = format;
  const { symbol: currencySymbol } = currency;

  const initialLoadWithCustomBPs = useRef<boolean>(false);
  const customBreakpointsGridRef = useRef<CustomBreakpointsData | null>(null);

  const updatedCaptableInfoForCurrent = useMemo(() => {
    if (typeof updatedCaptableInfo === 'string') {
      return updatedCaptableInfo;
    }
    if (!updatedCaptableInfo?.id || updatedCaptableInfo.id?.toString() !== captableInfo.id?.toString()) {
      return {};
    }
    return updatedCaptableInfo;
  }, [updatedCaptableInfo, captableInfo.id]);

  const hasUpdatedInfoForCurrent =
    typeof updatedCaptableInfoForCurrent !== 'string' && !isEmpty(updatedCaptableInfoForCurrent);

  const capTableURL = useMemo(() => captableUrl(firmSlugParam!, companySlugParam!), [firmSlugParam, companySlugParam]);

  const capTableNavItems = useCapTableTertiaryNavItems(firmSlugParam ?? '', companySlugParam ?? '');

  const handleSaveMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setSaveMenuAnchorEl(event.currentTarget);
  }, []);

  const breakpointSaveRef = useRef<(() => void | Promise<void>) | null>(null);
  breakpointSaveRef.current = breakpointSaveState?.save ?? null;
  // Custom BP opens sync confirm on final allocation; waterfall is async.
  const stableBreakpointSave = useCallback((): void | Promise<void> => {
    const skipPromise = Boolean(isAllocationFinal && isCustomSaveState(breakpointSaveState));
    return invokeNavigationSave(breakpointSaveRef.current, skipPromise);
  }, [breakpointSaveState, isAllocationFinal]);

  const tertiaryNavState = useMemo(() => {
    if (isEmpty(captableInfo)) {
      return null;
    }
    const actions = isAllocationFinal !== undefined && breakpointSaveState
      ? [
        {
          type: 'splitButton' as const,
          label: 'Save',
          variant: 'positive' as const,
          disabled: breakpointSaveState.disabled,
          isSaving: isCreatingProforma,
          locked: breakpointSaveState.locked,
          menuAriaLabel: 'Save options',
          onClick: stableBreakpointSave,
          ...(isCustomSaveState(breakpointSaveState) && breakpointSaveState.isProformaAvailable
            ? { onMenuClick: handleSaveMenuClick }
            : {}),
        },
      ]
      : [];
    return {
      ariaLabel: 'Breakpoint Analysis',
      items: capTableNavItems,
      actions,
    };
  }, [captableInfo, capTableNavItems, breakpointSaveState, isAllocationFinal, isCreatingProforma, handleSaveMenuClick, stableBreakpointSave]);

  useTertiaryNavigationRegistration(tertiaryNavState);

  const getAllocationStatus = async () => {
    try {
      if (!captableInfo.id) {
        return;
      }
      const response = await captableAction.verifyIfHasFinalAllocation(captableInfo.id);
      setIsDisabled(!isEmpty(response) || viewOnlyUser);
      setIsAllocationFinal(!isEmpty(response));
      if (isExportMode) {
        setIsAllocationFinal(true);
        setIsDisabled(true);
      }
    } catch (error: any) {
      setIsDisabled(false);
      setIsAllocationFinal(false);
      throw new Error(error);
    }
  };

  useEffect(() => {
    if (captableInfo.id && !isEmpty(captableInfo.securities) && !hasUpdatedInfoForCurrent) {
      const optionConvertibleSecurities = captableInfo.securities?.filter(s =>
        [UNISSUED_OPTIONS, CONVERTIBLE_NOTES].includes(s.security_type ?? -1)
      );

      if (
        optionConvertibleSecurities &&
        captableInfo.securities &&
        optionConvertibleSecurities.length < captableInfo.securities.length
      ) {
        getCapTableBreakpoints(captableInfo.id);
      } else {
        setShowSecurityTypeMsg(true);
      }
    }
    if (captableInfo.id) {
      getAllocationStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo.id, viewOnlyUser, hasUpdatedInfoForCurrent]);

  useEffect(() => {
    if (!isEmpty(captableInfo)) {
      setCustomBreakpoints(captableInfo?.use_custom_breakpoints ?? false);
      initialLoadWithCustomBPs.current = captableInfo?.use_custom_breakpoints ?? false;
      setCustomBreakpointColumns(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo.id, captableInfo.use_custom_breakpoints, initialLoadWithCustomBPs]);

  const getColumnsData = (breakpointsData: BreakpointColumn[], setInitialValue = false): BreakpointColumn[] => {
    const getSerieData = (serieItem: BreakpointSeries) => {
      if (!Number.isNaN(serieItem.value)) {
        return {
          ...serieItem,
          value: setInitialValue ? 0 : serieItem.value,
        };
      }
      return serieItem;
    };

    return breakpointsData.map(bp => ({
      ...bp,
      price: setInitialValue ? 0 : bp.price,
      series: bp.series.map(serie => ({
        ...getSerieData(serie),
        security: serie.security,
      })),
      range: bp.range.replaceAll(worldCurrencies[DEFAULT_CURRENCY], currencySymbol),
    }));
  };

  const waterfallBreakpointsPrint = useMemo(() => {
    if (!captableInfo?.waterfall_breakpoints?.breakpoints || !captableInfo?.id) {
      return '';
    }
    return `${captableInfo.id}_${JSON.stringify(captableInfo.waterfall_breakpoints.breakpoints)}`;
  }, [captableInfo?.waterfall_breakpoints, captableInfo?.id]);

  useEffect(() => {
    if (waterfallBreakpointsPrint) {
      if (captableInfo?.waterfall_breakpoints?.breakpoints) {
        const waterfallBreakpoints = captableInfo.waterfall_breakpoints.breakpoints;
        const tmpBreakpoints = getColumnsData(waterfallBreakpoints);
        setWaterfallColumns(tmpBreakpoints);
      } else {
        setWaterfallColumns(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waterfallBreakpointsPrint]);

  useEffect(() => {
    if (!isEmpty(captableInfo) && customBreakpoints && !isEmpty(waterfallColumns)) {
      let tmpBreakpoints = null;
      if (captableInfo?.custom_breakpoints?.breakpoints) {
        const customBreakpointsData = captableInfo.custom_breakpoints.breakpoints;
        tmpBreakpoints = getColumnsData(customBreakpointsData);
        const lastBreakpoint = tmpBreakpoints.at(-1) as BreakpointColumn;
        const lastBreakpointSeries = lastBreakpoint.series;
        // Use current own percent as value for existing custom breakpoints when the final bp is PRO RATA
        const replacedSeries = replaceProRata(lastBreakpointSeries, captableInfo.securities ?? []);
        lastBreakpoint.series = replacedSeries;
        customBreakpointsGridRef.current = {
          use_custom_breakpoints: true,
          custom_breakpoints: {
            breakpoints: tmpBreakpoints,
            prices_per_share: tmpBreakpoints.map(bpCol => bpCol.price),
          },
        };
      } else {
        tmpBreakpoints = getColumnsData(waterfallColumns!, true);
      }
      setCustomBreakpointColumns(tmpBreakpoints);
    } else {
      setCustomBreakpointColumns(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo, customBreakpoints, waterfallColumns, customBreakpointsGridRef]);

  const breakPointAnalysisDetailTableProps = useMemo(
    () => ({
      waterfallColumns: waterfallColumns!,
      format,
      hideGrid: customBreakpoints,
      initialLoadWithCustomBPs,
      isDisabled: !!isDisabled,
      isAllocationFinal: !!isAllocationFinal,
      capTableId: captableInfo.id!,
      customBreakpointsGridRef,
    }),
    [captableInfo, waterfallColumns, format, customBreakpoints, initialLoadWithCustomBPs, isDisabled, isAllocationFinal]
  );

  const customBreakpointsDetailTableProps = {
    captableId: captableInfo.id!,
    hideGrid: !customBreakpoints,
    customBreakpointColumns: customBreakpointColumns!,
    selectedMeasurementDate,
    saveAsNewProforma,
    format,
    isAllocationFinal: !!isAllocationFinal,
    isDisabled: !!isDisabled,
    customBreakpointsGridRef,
  };

  const handleChangeSwitch = () => setCustomBreakpoints(customBPs => !customBPs);

  const breakpointColumns = customBreakpoints ? customBreakpointColumns : waterfallColumns;
  let breakpointExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (!captableInfo?.id || !hasUpdatedInfoForCurrent) {
    breakpointExportStatus = 'loading';
  } else if (isEmpty(captableInfo?.securities) || showSecurityTypeMsg || isEmpty(breakpointColumns)) {
    breakpointExportStatus = 'missing';
  }

  useExportSignal({
    target: PdfExportTargets.BREAKPOINT_ANALYSIS.id,
    status: breakpointExportStatus,
    enabled: isExportMode,
  });

  if (isEqual(updatedCaptableInfoForCurrent, ERROR_403)) {
    return <MessageBox title={FORBIDDEN_BP_ANALYSIS} tagline={GET_BP_ACCESS} fullWidth={false} />;
  }

  if (captableInfo?.id && (isEmpty(captableInfo.securities) || showSecurityTypeMsg)) {
    let title = 'The Cap Table is empty';
    let message = 'Please add a security first.';

    if (showSecurityTypeMsg) {
      title = CAP_TABLE_NEEDS_MORE_SECURITIES_TITLE;
      message = CAP_TABLE_NEEDS_MORE_SECURITIES_TAGLINE;
    }

    return (
      <MessageBox
        title={title}
        tagline={message}
        fullWidth={false}
        action={<GoToCapTable capTableURL={capTableURL} />}
      />
    );
  }

  if (!captableInfo?.id || !hasUpdatedInfoForCurrent) {
    return (
      <CustomContainer
        data-ref={PdfExportTargets.BREAKPOINT_ANALYSIS.id}
        style={isExportMode ? EXPORT_MODE_STYLES : {}}>
        <GridSkeleton />
      </CustomContainer>
    );
  }

  return (
    <Root>
      <Menu anchorEl={saveMenuAnchorEl} open={Boolean(saveMenuAnchorEl)} onClose={() => setSaveMenuAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setSaveMenuAnchorEl(null);
            if (isCustomSaveState(breakpointSaveState)) {
              breakpointSaveState.onProformaOpen();
            }
          }}>
          Save as New Proforma
        </MenuItem>
      </Menu>

      {/* Allocation Final Message */}
      {isAllocationFinal && isDisabled && (
        <AllocationFinalMessage
          tableTerms={{ tableName: 'Breakpoint Analysis' }}
          setIsDisabled={setIsDisabled}
          singleAlert
          finalAllocations={[]}
          viewOnlyUser={viewOnlyUser}
        />
      )}
      {isAllocationFinal && !isDisabled && <Alert isAlertVisible>{BREAKPOINT_ANALYSIS_LOCKED_MESSAGE}</Alert>}

      <FormGroup row className={classes.useCustomBreakpoints}>
        <FormControlLabel
          control={
            <Switch
              id="custom-breakpoints-switch"
              checked={customBreakpoints || false}
              onChange={handleChangeSwitch}
              name="useCustomBreakpoints"
              color="primary"
              disabled={isShowLoadingProgress || isDisabled}
              disableRipple
            />
          }
          label={
            <Typography variant="body2" color="textPrimary" className={classes.switchToggleLegend}>
              Use custom breakpoints?
            </Typography>
          }
        />
      </FormGroup>

      <CustomContainer
        data-ref={PdfExportTargets.BREAKPOINT_ANALYSIS.id}
        style={isExportMode ? EXPORT_MODE_STYLES : {}}>
        <Grid size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
          {customBreakpoints ? (
            <CustomBreakpoints {...customBreakpointsDetailTableProps} onSaveStateChange={setBreakpointSaveState} />
          ) : (
            <WaterfallBreakpoints {...breakPointAnalysisDetailTableProps} onSaveStateChange={setBreakpointSaveState} />
          )}
        </Grid>
      </CustomContainer>
    </Root>
  );
};

export default BreakpointAnalysis;
